import { useEffect, useMemo, useState } from "react";
import { Lock, BarChart3, Users, RefreshCw, Sparkles } from "lucide-react";
import useFetchStats from "../hooks/useFetchStats";
import stakingPowerChart from "../assets/staking-power-chart.png";

const EXTENDED_STATS_ENDPOINT = "/project/api/extended-stats";
const MOBULA_MARKET_ENDPOINT = "/project/api/mobula-market";
const MOBULA_VOLUME_ENDPOINT = "/project/api/mobula-volume";
const DEXSCREENER_RYS_PAIR_ENDPOINT =
  "https://api.dexscreener.com/latest/dex/pairs/solana/BuX9TN5doE5hCqpcmqMKYkidXC8zgBK5wHHKujdaAbiQ";
const DEXSCREENER_PUMPSWAP_ENDPOINT =
  "https://api.dexscreener.com/latest/dex/search?q=pumpswap";

const FALLBACK_EXTENDED = {
  totalStakes: 18542,
  compoundOptIns: 107,
  buybacksSol: 50.43,
  allTimeVolumeUsd: 50_000_000,
};

const FALLBACK_MOBULA = {
  totalVolumeUsd: 50_000_000,
  liquidityUsd: 80_000,
  marketCapUsd: 700_000,
};

const FALLBACK_LIQUIDITY = {
  rys: {
    liquidityUsd: 150_000,
    marketCap: 700_000,
    liquidityBase: 90_000_000,
  },
  peers: [
    { name: "Nova", liquidityUsd: 45_000, marketCap: 680_000 },
    { name: "Apex", liquidityUsd: 32_000, marketCap: 640_000 },
    { name: "Drift", liquidityUsd: 28_000, marketCap: 610_000 },
  ],
};

const FALLBACK_BUYBACKS = {
  totalSol: FALLBACK_EXTENDED.buybacksSol,
  totalRys: 0,
  averageMonthlySol: FALLBACK_EXTENDED.buybacksSol / 12,
  since: null,
};

const STAKER_BUYBACKS_ENDPOINT = "/project/api/staker-buybacks";

const formatUsd = (value, { compact = false } = {}) => {
  const numeric = Number(value) || 0;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    notation: compact ? "compact" : "standard",
    maximumFractionDigits: compact ? 1 : numeric >= 1000 ? 0 : 2,
  }).format(Math.max(numeric, 0));
};

const formatNumber = (value, options = {}) => {
  const numeric = Number(value) || 0;
  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 2,
    minimumFractionDigits: options.minimumFractionDigits ?? 0,
  }).format(Math.max(numeric, 0));
};

const LiquidityBar = ({
  label,
  ratio,
  comment,
  highlight,
  coverage,
  relativeGap,
  url,
}) => {
  const barWidth = Math.min(Math.max(ratio, 0), 1) * 100;
  return (
    <div
      className={`glass rounded-2xl p-4 transition-all duration-300 ${
        highlight ? "border border-purple-400/60 shadow-lg shadow-purple-500/20" : ""
      }`}
    >
      <div className="flex items-center justify-between text-sm mb-3">
        {url ? (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className={`font-semibold underline decoration-dotted underline-offset-2 hover:decoration-solid transition-all ${
              highlight
                ? "text-purple-200 hover:text-purple-100"
                : "text-gray-300 hover:text-white"
            }`}
          >
            {label} ↗
          </a>
        ) : (
          <span className={`font-semibold ${highlight ? "text-purple-200" : "text-gray-300"}`}>
            {label}
          </span>
        )}
        {typeof coverage === "number" && (
          <span className="text-gray-400">{(coverage * 100).toFixed(1)}% depth</span>
        )}
      </div>
      <div className="h-1.5 w-full rounded-full bg-white/10 overflow-hidden">
        <div
          className={`h-full ${
            highlight ? "bg-gradient-to-r from-purple-400 to-pink-500" : "bg-white/30"
          }`}
          style={{ width: `${barWidth}%` }}
        />
      </div>
      <p className="text-xs text-gray-400 mt-2">{comment}</p>
      {relativeGap && (
        <p className="text-[11px] text-purple-200 mt-1">
          RYS books are {relativeGap.toFixed(1)}x deeper
        </p>
      )}
    </div>
  );
};

export default function StakingPower() {
  // useFetchStats returns { data, loading, error } (not the data object directly)
  const { data: stats } = useFetchStats();
  const [extendedStats, setExtendedStats] = useState(FALLBACK_EXTENDED);
  const [mobulaStats, setMobulaStats] = useState(FALLBACK_MOBULA);
  const [liquiditySnapshots, setLiquiditySnapshots] = useState(FALLBACK_LIQUIDITY);
  const [buybackStats, setBuybackStats] = useState(FALLBACK_BUYBACKS);

  useEffect(() => {
    let isMounted = true;

    const fetchExtended = async () => {
      try {
        const response = await fetch(EXTENDED_STATS_ENDPOINT);
        if (!response.ok) throw new Error("Extended stats request failed");

        const payload = await response.json();
        if (!payload?.data) throw new Error("Extended stats payload invalid");

        if (isMounted) {
          setExtendedStats({
            ...FALLBACK_EXTENDED,
            ...payload.data,
            compoundOptIns:
              payload.data.compoundOptIns ??
              payload.data.totalStakes ??
              FALLBACK_EXTENDED.compoundOptIns,
          });
        }
      } catch (error) {
        // Keep fallback data so section never feels empty
        console.warn("Using fallback extended stats:", error);
        if (isMounted) {
          setExtendedStats(FALLBACK_EXTENDED);
        }
      }
    };

    fetchExtended();
    // Poll every 10 seconds (backend serves cached extended stats, refreshed every 15s)
    const interval = setInterval(fetchExtended, 10000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchBuybacks = async () => {
      try {
        const response = await fetch(STAKER_BUYBACKS_ENDPOINT);
        if (!response.ok) {
          throw new Error(`Buyback request failed: ${response.status}`);
        }
        const payload = await response.json();
        if (!payload?.success || !payload?.data) {
          throw new Error("Malformed buyback response");
        }

        if (!isMounted) return;

        setBuybackStats({
          totalSol: Number(payload.data.totalSol) || FALLBACK_BUYBACKS.totalSol,
          totalRys: Number(payload.data.totalRys) || FALLBACK_BUYBACKS.totalRys,
          averageMonthlySol:
            Number(payload.data.averageMonthlySol) || FALLBACK_BUYBACKS.averageMonthlySol,
          since: payload.data.since || null,
          wallet: payload.data.wallet,
        });
      } catch (error) {
        console.warn("Compound buybacks fallback:", error);
        if (!isMounted) return;

        const fallbackTotal =
          extendedStats.buybacksSol || FALLBACK_BUYBACKS.totalSol;
        setBuybackStats({
          totalSol: fallbackTotal,
          totalRys: FALLBACK_BUYBACKS.totalRys,
          averageMonthlySol:
            fallbackTotal > 0 ? fallbackTotal / 12 : FALLBACK_BUYBACKS.averageMonthlySol,
          since: null,
          wallet: null,
        });
      }
    };

    fetchBuybacks();
    const interval = setInterval(fetchBuybacks, 15 * 60 * 1000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [extendedStats.buybacksSol]);

  useEffect(() => {
    let isMounted = true;

    const fetchMobulaMetrics = async () => {
      const nextStats = { ...FALLBACK_MOBULA };

      try {
        const response = await fetch(MOBULA_MARKET_ENDPOINT);
        if (response.ok) {
          const payload = await response.json();
          const data = payload?.data;
          if (data) {
            nextStats.liquidityUsd =
              Number(data.liquidity) || nextStats.liquidityUsd;
            nextStats.marketCapUsd =
              Number(data.marketCap) || nextStats.marketCapUsd;
          }
        }
      } catch (error) {
        console.warn("Mobula market data fallback:", error);
      }

      try {
        const response = await fetch(MOBULA_VOLUME_ENDPOINT);
        if (response.ok) {
          const payload = await response.json();
          const totalVolume = Number(payload?.data?.totalVolumeUsd) || 0;
          if (totalVolume) {
            nextStats.totalVolumeUsd = totalVolume;
          }
        }
      } catch (error) {
        console.warn("Mobula OHLCV fallback:", error);
      }

      if (isMounted) {
        setMobulaStats(nextStats);
      }
    };

    fetchMobulaMetrics();
    const interval = setInterval(fetchMobulaMetrics, 30 * 60 * 1000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchDexscreenerSnapshots = async () => {
      try {
        const [rysResponse, pumpsResponse] = await Promise.all([
          fetch(DEXSCREENER_RYS_PAIR_ENDPOINT),
          fetch(DEXSCREENER_PUMPSWAP_ENDPOINT),
        ]);

        const rysJson = await rysResponse.json();
        const pumpsJson = await pumpsResponse.json();

        const rysPair = rysJson?.pair || rysJson?.pairs?.[0];
        const rysLiquidityUsd =
          Number(rysPair?.liquidity?.usd) || FALLBACK_LIQUIDITY.rys.liquidityUsd;
        const rysLiquidityBase =
          Number(rysPair?.liquidity?.base) || FALLBACK_LIQUIDITY.rys.liquidityBase;
        const rysMarketCap =
          Number(rysPair?.marketCap) || FALLBACK_LIQUIDITY.rys.marketCap;
        const rysAddress = rysPair?.baseToken?.address;

        const peerCandidates = (pumpsJson?.pairs || [])
          .filter((pair) => pair.dexId === "pumpswap" && pair.chainId === "solana")
          .filter((pair) => pair.baseToken?.address !== rysAddress)
          .map((pair) => ({
            name: pair.baseToken?.symbol || pair.baseToken?.name || "Peer",
            liquidityUsd: Number(pair?.liquidity?.usd) || 0,
            marketCap: Number(pair?.marketCap) || 0,
            url: pair?.url || null,
          }))
          .filter((peer) => peer.liquidityUsd > 0 && peer.marketCap > 0)
          .filter((peer) =>
            rysMarketCap ? Math.abs(peer.marketCap - rysMarketCap) / rysMarketCap <= 0.45 : true
          )
          .sort((a, b) => a.liquidityUsd - b.liquidityUsd)
          .slice(0, 3);

        if (isMounted) {
          setLiquiditySnapshots({
            rys: {
              liquidityUsd: rysLiquidityUsd,
              liquidityBase: rysLiquidityBase,
              marketCap: rysMarketCap,
            },
            peers: peerCandidates.length ? peerCandidates : FALLBACK_LIQUIDITY.peers,
          });
        }
      } catch (error) {
        console.warn("Dexscreener fallback:", error);
        if (isMounted) {
          setLiquiditySnapshots(FALLBACK_LIQUIDITY);
        }
      }
    };

    fetchDexscreenerSnapshots();
    const interval = setInterval(fetchDexscreenerSnapshots, 5 * 60 * 1000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, []);

  const stakingPercent = Math.max(Number(stats.staked) || 0, 0);
  const burnedPercent = Math.max(Number(stats.burned) || 0, 0);
  const lpPercent =
    stats.totalSupply &&
    liquiditySnapshots.rys?.liquidityBase &&
    stats.totalSupply > 0
      ? (liquiditySnapshots.rys.liquidityBase / stats.totalSupply) * 100
      : 0;

  const lockedPercent = Math.min(stakingPercent + burnedPercent + lpPercent, 100);
  const circulatingPercent = Math.max(100 - lockedPercent, 0);
  const growthMultiplier =
    circulatingPercent > 0 ? (100 / circulatingPercent).toFixed(1) : "∞";

  const lockedBreakdown = [
    { label: "Staked", value: stakingPercent },
    { label: "Burned", value: burnedPercent },
    ...(lpPercent
      ? [
          {
            label: "LP Locked",
            value: lpPercent,
          },
        ]
      : []),
  ].filter((item) => item.value > 0.01);

  const compoundUsers =
    extendedStats.compoundOptIns ??
    extendedStats.totalStakes ??
    Math.round((Number(stats.users) || 0) * 0.18);

  const liquidityComparisons = useMemo(() => {
    const baselineLiquidity =
      liquiditySnapshots.rys?.liquidityUsd || FALLBACK_LIQUIDITY.rys.liquidityUsd;
    const baselineMarketCap =
      liquiditySnapshots.rys?.marketCap || FALLBACK_LIQUIDITY.rys.marketCap;

    const items = [
      {
        label: "RYS",
        ratio: 1,
        comment: `~${formatUsd(baselineLiquidity, { compact: true })} liquidity supporting ${formatUsd(
          baselineMarketCap,
          { compact: true }
        )} market cap.`,
        highlight: true,
        coverage:
          baselineMarketCap > 0 ? baselineLiquidity / baselineMarketCap : null,
        url: "https://dexscreener.com/solana/bux9tn5doe5hcqpcmqmkykidxc8zgbk5whhkujdaabiq",
      },
    ];

    liquiditySnapshots.peers.forEach((peer) => {
      const relativeRatio = baselineLiquidity
        ? (peer.liquidityUsd || 0) / baselineLiquidity
        : 0;
      const coverage =
        peer.marketCap > 0 ? (peer.liquidityUsd || 0) / peer.marketCap : null;
      const relativeGap =
        baselineLiquidity && peer.liquidityUsd
          ? baselineLiquidity / peer.liquidityUsd
          : null;

      items.push({
        label: peer.name,
        ratio: relativeRatio,
        comment: `Only ${formatUsd(peer.liquidityUsd, { compact: true })} of liquidity on a ${formatUsd(
          peer.marketCap,
          { compact: true }
        )} cap.`,
        highlight: false,
        coverage,
        relativeGap: relativeGap && relativeGap > 1 ? relativeGap : null,
        url: peer.url || null,
      });
    });

    return items;
  }, [liquiditySnapshots]);

  const formattedVolume = formatUsd(
    mobulaStats.totalVolumeUsd || FALLBACK_MOBULA.totalVolumeUsd,
    { compact: true }
  );
  const sinceLabel = buybackStats.since
    ? new Date(buybackStats.since).toLocaleDateString("en-US", {
        month: "short",
        year: "numeric",
      })
    : null;

  return (
    <section
      id="staking-power"
      className="relative py-20 bg-[#08080d] border-y border-white/5 overflow-hidden"
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(147,51,234,0.18),_transparent_55%)] pointer-events-none" />
      <div className="relative max-w-6xl mx-auto px-6">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <p className="text-sm uppercase tracking-[0.5em] text-purple-300/70 mb-4">
            Staking Power
          </p>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-4">
            Locked supply turns every buy into amplified upside.
          </h2>
          <p className="text-base text-gray-400">
            Our staking program keeps most tokens off the market, funnels SOL rewards
            back into $RYS, and leverages one of the most liquid order books for a
            project in this market-cap range.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14">
          <div className="glass rounded-3xl p-6 border border-white/5">
            <div className="flex items-center gap-3 mb-4">
              <Lock className="w-5 h-5 text-purple-400" />
              <p className="text-sm uppercase tracking-wider text-gray-400">Locked Supply</p>
            </div>
            <p className="text-4xl font-bold text-white mb-2">
              {lockedPercent.toFixed(2)}%
            </p>
            <p className="text-sm text-gray-400">
              Only {circulatingPercent.toFixed(2)}% of the supply circulates, making price
              appreciation roughly {growthMultiplier}x easier.
            </p>
            <div className="mt-4 space-y-2 text-xs text-gray-400">
              {lockedBreakdown.map(({ label, value }) => (
                <div key={label} className="flex items-center justify-between">
                  <span>{label}</span>
                  <span className="text-white">{value.toFixed(2)}%</span>
                </div>
              ))}
            </div>
          </div>

          <div className="glass rounded-3xl p-6 border border-white/5">
            <div className="flex items-center gap-3 mb-4">
              <Users className="w-5 h-5 text-purple-400" />
              <p className="text-sm uppercase tracking-wider text-gray-400">Compound Stakers</p>
            </div>
            <p className="text-4xl font-bold text-white mb-2">
              {compoundUsers.toLocaleString()}
            </p>
            <p className="text-sm text-gray-400">
              Wallets choosing rewards in $RYS rather than SOL. Their auto-compounding
              converts SOL payouts into daily buy pressure.
            </p>
          </div>

          <div className="glass rounded-3xl p-6 border border-white/5">
            <div className="flex items-center gap-3 mb-2">
              <RefreshCw className="w-5 h-5 text-purple-400" />
              <div>
                <p className="text-sm uppercase tracking-wider text-gray-400">
                  Staking Buybacks
                </p>
                {sinceLabel && (
                  <p className="text-[11px] text-gray-500">Tracking since {sinceLabel}</p>
                )}
              </div>
            </div>
            <p className="text-4xl font-bold text-white mb-1">
              {formatNumber(buybackStats.totalSol, { minimumFractionDigits: 0 })} SOL
            </p>
            <p className="text-xs text-gray-400 mb-2">
              Wallet buys routed from daily compound staking harvests.
            </p>
            <p className="text-sm text-purple-200">
              ≈ {formatNumber(buybackStats.averageMonthlySol, { minimumFractionDigits: 1 })} SOL / month
              {buybackStats.totalRys > 0 && (
                <>
                  {" · "}
                  {formatNumber(buybackStats.totalRys, { minimumFractionDigits: 0 })} RYS
                </>
              )}
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-8 items-center">
          <div className="glass rounded-3xl p-6 border border-white/5">
            <div className="flex items-center gap-3 mb-6">
              <BarChart3 className="w-5 h-5 text-purple-400" />
              <div>
                <p className="text-sm uppercase tracking-wider text-gray-400">Liquidity vs Peers</p>
                <p className="text-xs text-gray-500">
                  Live PumpSwap data (Dexscreener) for similar market-cap tokens.
                </p>
              </div>
            </div>
            <div className="space-y-4">
              {liquidityComparisons.map((item) => (
                <LiquidityBar
                  key={item.label}
                  label={item.label}
                  ratio={item.ratio}
                  comment={item.comment}
                  highlight={item.highlight}
                  coverage={item.coverage}
                  relativeGap={item.relativeGap}
                  url={item.url}
                />
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-6">
            <div className="glass rounded-3xl p-6 border border-white/5 flex flex-col gap-6">
              <div className="flex items-center gap-3">
                <Sparkles className="w-5 h-5 text-purple-400" />
                <p className="text-sm uppercase tracking-wider text-gray-400">
                  Why it matters
                </p>
              </div>
              <ul className="space-y-4 text-gray-300 text-sm leading-relaxed">
                <li>
                  <span className="text-white font-semibold">{formattedVolume}+</span> lifetime
                  volume keeps books thick so big buys move price, not
                  slippage.
                </li>
                <li>
                  Compound stakers recycle SOL rewards into fresh $RYS every day, creating
                  permanent demand without treasury spending.
                </li>
                <li>
                  With most supply locked away, every organic buy and each scheduled
                  buyback travels further — perfect setup for long-term holders.
                </li>
              </ul>
            </div>
            <figure className="relative glass rounded-3xl border border-white/5 overflow-hidden group">
              <div className="absolute inset-0 bg-gradient-to-b from-purple-500/20 via-transparent to-black/60 pointer-events-none" />
              <div className="absolute inset-0 rounded-3xl ring-1 ring-purple-500/30 opacity-0 group-hover:opacity-75 blur-2xl transition duration-500 pointer-events-none" />
              <img
                src={stakingPowerChart}
                alt="Live RYS/SOL chart showing continuous buy activity"
                className="w-full h-full object-cover scale-[1.01] saturate-125 transition-transform duration-700 group-hover:scale-[1.03]"
                loading="lazy"
              />
              <div className="absolute top-4 left-4 bg-black/70 border border-purple-400/40 px-3 py-1.5 rounded-full text-xs font-semibold tracking-wide text-purple-100 shadow-lg shadow-purple-900/40">
                Auto-buy pressure
              </div>
              <div className="absolute bottom-16 right-4 bg-black/70 border border-white/10 rounded-2xl px-4 py-3 backdrop-blur-sm shadow-lg shadow-black/40 text-right max-w-[220px]">
                <p className="text-[10px] uppercase tracking-[0.3em] text-gray-400 mb-1">
                  Daily buys
                </p>
                <p className="text-base font-semibold text-white">Compound stakers</p>
                <p className="text-[11px] text-gray-300">
                  Every SOL reward cycles into green candles.
                </p>
              </div>
              <figcaption className="relative p-4 text-xs text-gray-300 bg-black/50 border-t border-white/5">
                Day after day the staking program routes SOL rewards into market buys,
                leaving a constant stream of green on the chart.
              </figcaption>
            </figure>
          </div>
        </div>
      </div>
    </section>
  );
}
